import type { Ref } from 'vue';
import { Store } from '@tanstack/store';
// React twin: swap the next line for `import { useStore } from '@tanstack/react-store';`
// — the `Store` core above is framework-agnostic, so only this one adapter import
// changes on the React/Expo port. `useSelector` is the non-deprecated alias of the
// `useStore` accessor; both share an identical signature.
import { useSelector } from '@tanstack/vue-store';

/**
 * Persistence config for a {@link createAppStore} store. State is written to
 * localStorage under a `{ v, s }` envelope so a shape change can be detected and
 * reset via {@link PersistOptions.version}.
 */
export interface PersistOptions<T> {
  /** localStorage key the `{ v, s }` envelope is written to. */
  key: string;
  /** Bump to invalidate stale persisted shapes — a mismatch resets to initial state. */
  version: number;
  /** Persist only a subset of state (default: the whole state object). */
  pick?: (state: T) => Partial<T>;
  /** Hydrate strategy when a persisted subset is read back (default: shallow merge). */
  merge?: (initial: T, persisted: Partial<T>) => T;
}

/**
 * A typed TanStack Store wrapper. State is a plain serializable object; derive
 * via {@link AppStore.useSelector} and mutate only through {@link AppStore.setState}.
 */
export interface AppStore<T extends object> {
  /** The underlying framework-agnostic TanStack {@link Store}. */
  store: Store<T>;
  /**
   * Vue accessor: returns a `Readonly<Ref<S>>` that updates synchronously when
   * `setState` runs (same tick). Omit the selector to read the whole state.
   */
  useSelector: <S = T>(selector?: (state: T) => S) => Readonly<Ref<S>>;
  /** Replace state immutably; subscribers (and persistence) fire synchronously. */
  setState: (updater: (prev: T) => T) => void;
  /** Reset to the original `initialState` and clear any persisted copy. */
  reset: () => void;
}

/** Shape of the persisted localStorage envelope. */
interface PersistEnvelope<T> {
  v: number;
  s: Partial<T>;
}

/**
 * Create a typed client-state store backed by `@tanstack/store` with optional
 * versioned localStorage persistence. Designed for true client state (UI-ephemeral
 * or cross-route) — server state belongs in TanStack Query, not here.
 *
 * Persistence is corrupt-JSON safe (falls back to initial state, never throws at
 * module init), swallows quota / private-mode write errors (state still works
 * in-memory), and supports `pick` subsetting.
 */
export function createAppStore<T extends object>(opts: {
  /** Devtools/debug label (also future React-devtools label). */
  name: string;
  initialState: T;
  persist?: PersistOptions<T>;
}): AppStore<T> {
  const hydrate = (): T => {
    const { persist } = opts;
    if (!persist) return opts.initialState;
    try {
      const raw = localStorage.getItem(persist.key);
      if (!raw) return opts.initialState;
      const parsed = JSON.parse(raw) as PersistEnvelope<T>;
      // Version mismatch (or a malformed envelope without a numeric `v`) → reset.
      if (parsed.v !== persist.version) return opts.initialState;
      const persisted = parsed.s ?? {};
      return persist.merge
        ? persist.merge(opts.initialState, persisted)
        : { ...opts.initialState, ...persisted };
    } catch {
      // Corrupt JSON / unavailable storage → fresh state. Never throw at init.
      return opts.initialState;
    }
  };

  const store = new Store<T>(hydrate());

  if (opts.persist) {
    const { key, version, pick } = opts.persist;
    store.subscribe(() => {
      try {
        const snapshot = pick ? pick(store.state) : store.state;
        const envelope: PersistEnvelope<T> = { v: version, s: snapshot };
        localStorage.setItem(key, JSON.stringify(envelope));
      } catch {
        // Quota exceeded / private mode: keep working in-memory, drop the write.
      }
    });
  }

  return {
    store,
    useSelector: (selector) => useSelector(store, selector),
    setState: (updater) => store.setState(updater),
    reset: () => {
      store.setState(() => opts.initialState);
      if (opts.persist) {
        try {
          localStorage.removeItem(opts.persist.key);
        } catch {
          // Ignore storage unavailability on reset.
        }
      }
    },
  };
}
